import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Seo, { breadcrumbSchema } from '@/components/Seo';
import Breadcrumbs from '@/components/Breadcrumbs';
import SectionHeading from '@/components/SectionHeading';
import Reveal from '@/components/Reveal';
import CTAPanel from '@/components/CTAPanel';
import { IMAGES, BUILDER_STATS, HIGHLIGHTS } from '@/data/siteData';

const Overview = () => (
  <>
    <Seo
      title="Provident Palm Vista Overview | 16 Acre Tropical Ecosystem Shilphata"
      description="Provident Palm Vista overview — a 16-acre tropical residential ecosystem by Provident Housing (Puravankara) on Kalyan-Shilphata Road with 1, 2 & 2.5 BHK balcony homes, 70% open space and 60+ amenities."
      path="/overview"
      jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Overview', path: '/overview' }])]}
    />
    <section className="pt-28 md:pt-36 pb-16 md:pb-24 bg-ivory">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ name: 'Overview' }]} />
        <Reveal>
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-gold">Project Overview</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-palm mt-3 leading-[1.05] tracking-tight">
            Provident Palm Vista — <span className="italic text-gold-grad">a future perfect life</span>
          </h1>
        </Reveal>
        <div className="grid lg:grid-cols-12 gap-10 mt-12">
          <Reveal className="lg:col-span-7 space-y-5 text-[15px] leading-relaxed text-ink/80">
            <p>
              Provident Palm Vista is a themed tropical residential ecosystem spread across 16 acres on Kalyan–Shilphata Road, Mumbai – 421204. Developed by Provident Housing, a Puravankara company, the project offers spacious 1, 2 and 2.5 BHK homes — each designed with a balcony — within a landscape that keeps 70% of the development open.
            </p>
            <p>
              The master plan organises residential towers around a central clubhouse and amenity spine, with wings facing a mix of city views and hill views. Homes are arranged so that daily life flows outward — into decks, lawns, water edges, walking trails and the 30 varieties of palm trees that give the project its name.
            </p>
            <h2 className="font-display text-2xl sm:text-3xl font-medium text-palm pt-4">The concept: resort calm, city access</h2>
            <p>
              Most mid-segment projects treat amenities as a checklist. Palm Vista treats them as an ecosystem. Kids get their own zone — a play area, climbing wall, cricket nets, tricycle track, floor games, a labyrinth and a kids’ pool. Adults get a swimming pool, jogging and walking tracks, a multi-play court, cafe deck, Wi-Fi lounge, putting lawn and a reflexology pathway. Seniors get dedicated decks, a spill-over lawn, reading and working coves, and shaded ledge and cove seating.
            </p>
            <p>
              The location multiplies that lifestyle. About 5 minutes from Shilphata Junction, the project connects to Diva Station (~3 mins), Thane–Belapur Road (~15 mins), the Eastern Express Highway (~20 mins) and the employment hubs of Airoli and Ghansoli. Schools such as Guardians School and hospitals such as MGM Hospital are minutes away. Full details are on the location page.
            </p>
            <h2 className="font-display text-2xl sm:text-3xl font-medium text-palm pt-4">About the developer</h2>
            <p>
              Provident Housing is the value-homes arm of Puravankara Limited, one of India’s established real estate groups. The group brings 49 years of real estate experience, a presence across 9 Indian cities, 83+ projects and over 42,500 homes delivered to more than 1.7 lakh happy families. In Mumbai and Pune, the group’s portfolio includes Purva Clermont (Chembur), Purva Emerald Bay (Mundhwa), Purva Aspire and Provident Kenvista (Kondhwa).
            </p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5 space-y-5">
            <img src={IMAGES.masterPlan} alt="Provident Palm Vista master plan — towers, clubhouse and amenity spine" className="rounded-3xl w-full shadow-xl border border-line" loading="lazy" width={664} height={849} />
            <img src={IMAGES.builderProject} alt="A delivered project by the Puravankara group" className="rounded-3xl w-full object-cover shadow-xl border border-line" loading="lazy" width={458} height={441} />
            <div className="bg-palm rounded-3xl p-7 text-ivory">
              <p className="text-xs font-bold tracking-[0.25em] uppercase text-gold">Provident · A Puravankara Company</p>
              <div className="grid grid-cols-2 gap-5 mt-5">
                {BUILDER_STATS.map((s) => (
                  <div key={s.label}>
                    <p className="font-display text-3xl text-gold font-medium">{s.num}</p>
                    <p className="text-[11px] text-ivory/60 mt-1 leading-snug">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-16">
          {HIGHLIGHTS.map((h, i) => (
            <Reveal key={h.label} delay={i * 0.05}>
              <div className="bg-white border border-line rounded-2xl p-6 h-full">
                <p className="font-display text-4xl font-medium text-palm">{h.num}<span className="text-xl text-gold">{h.unit}</span></p>
                <p className="font-semibold text-palm text-sm mt-2">{h.label}</p>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{h.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-12 flex flex-wrap gap-4">
          <Link to="/1-bhk" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">1 BHK homes <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/2-bhk" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">2 BHK homes <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/2-5-bhk" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">2.5 BHK homes <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/provident-palm-vista-floor-plan" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">View floor plans <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/provident-palm-vista-amenities" className="inline-flex items-center gap-2 text-sm font-semibold text-palm hover:text-gold transition-colors">Explore amenities <ArrowRight className="h-4 w-4" /></Link>
        </Reveal>
      </div>
    </section>
    <CTAPanel />
  </>
);

export default Overview;
